import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL;

// A "key metric" editor: metric type (from the seeded tool-metric taxonomy) +
// value + unit. value = { metricType, metricValue, metricUnit }; onChange(next).
// Pass metricTypes to reuse a shared fetch; otherwise it fetches them once.
// A "clean" suggested unit (e.g. Nm, mm, GB) auto-fills when the type is picked
// and the unit is empty; descriptive units (e.g. "varies (mm, mV)") are shown
// only as the unit placeholder.
const isCleanUnit = (u) => !!u && /^[A-Za-z%°Ω/µ.]+$/.test(u);

export default function MetricInput({ value, onChange, getToken, metricTypes, accent = '#2dd4bf' }) {
  const [types, setTypes] = useState(metricTypes || []);

  useEffect(() => {
    if (metricTypes) { setTypes(metricTypes); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await window.fetch(`${API_BASE}/quality/tool-metric-types`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await res.json();
        if (!cancelled && d.ok) setTypes(d.metricTypes || []);
      } catch { /* leave empty */ }
    })();
    return () => { cancelled = true; };
  }, [metricTypes, getToken]);

  const v = value || {};
  const typeDef = types.find(t => t.key === v.metricType);
  const unitHint = typeDef ? typeDef.unit : '';

  const onType = (key) => {
    const def = types.find(t => t.key === key);
    const next = { metricType: key, metricValue: v.metricValue || '', metricUnit: v.metricUnit || '' };
    if (!next.metricUnit && def && isCleanUnit(def.unit)) next.metricUnit = def.unit;
    onChange(next);
  };

  const inp = {
    background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)',
    borderRadius: 6, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box',
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.9fr', gap: 8 }}>
      <select value={v.metricType || ''} onChange={e => onType(e.target.value)} style={inp} title={typeDef ? typeDef.purpose : ''}>
        <option value="">Key metric…</option>
        {types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
      </select>
      <input value={v.metricValue || ''} onChange={e => onChange({ ...v, metricValue: e.target.value })}
        placeholder="Value" style={inp} />
      <input value={v.metricUnit || ''} onChange={e => onChange({ ...v, metricUnit: e.target.value })}
        placeholder={unitHint || 'Unit'} style={inp} title={unitHint ? `Suggested: ${unitHint}` : ''} />
    </div>
  );
}
